import { TFunction } from 'i18next';

import { Badge } from '@/components/ui/badge';
import { ConsignmentTableData } from '@/pages/consignments/model/types';
import { ColumnPreset, DataTableColumn } from '@/shared/ui/data-table';

export const getColumns = (t: TFunction): DataTableColumn<ConsignmentTableData>[] => [
  {
    id: 'cmrNumber',
    accessorKey: 'cmrNumber',
    header: t('columns.consignmentNumberDesc'),
    description: 'CMR / Consignment number',
    width: 'min-w-[140px]',
    cell: (row) => <span className="font-mono text-sm">{row.cmrNumber}</span>,
    enableSorting: true,
    sortFieldId: 'cmrNumber',
    visible: false
  },
  {
    id: 'statusCode',
    header: 'Status',
    accessorKey: 'statusName',
    description: 'Request status',
    width: 'min-w-[120px]',
    cell: (row) => {
      const getVariant = (code: string) => {
        switch (code) {
          case 'AC':
            return 'default';
          case 'NR':
            return 'secondary';
          case 'RC':
            return 'outline';
          default:
            return 'secondary';
        }
      };
      return <Badge variant={getVariant(row.statusCode)}>{row.statusName || row.statusCode}</Badge>;
    },
    enableSorting: true,
    sortFieldId: 'status'
  },
  {
    id: 'senderOrgName',
    header: 'Sender',
    description: 'Sender organization',
    width: 'min-w-[150px] max-w-[250px] whitespace-normal',
    cell: (row) => row.senderContact?.organization?.name || '-',
    enableSorting: true,
    sortFieldId: 'sender.org.name'
  },
  {
    id: 'receiverOrgName',
    header: 'Receiver',
    description: 'Receiver organization',
    width: 'min-w-[150px] max-w-[250px] whitespace-normal',
    cell: (row) => row.receiverContact?.organization?.name || '-',
    enableSorting: true,
    sortFieldId: 'receiver.org.name'
  },
  {
    id: 'destinationLocationDisplayName',
    accessorKey: 'destinationLocationDisplayName',
    header: 'Destination',
    description: 'Destination location',
    width: 'min-w-[200px] max-w-[300px] whitespace-normal'
  },
  {
    id: 'createdTime',
    accessorKey: 'createdTime',
    header: 'Created',
    description: 'Creation date',
    width: 'min-w-[110px]',
    cell: (row) => {
      const date = new Date(row.createdTime);
      return date.toLocaleDateString();
    },
    enableSorting: true,
    sortFieldId: 'createdTime'
  },
  {
    id: 'totalWeightKg',
    accessorKey: 'totalWeightKg',
    header: 'Weight (kg)',
    description: 'Total weight in kilograms',
    width: 'min-w-[100px]',
    cell: (row) => row.totalWeightKg?.toFixed(2) || '-',
    enableSorting: true,
    sortFieldId: 'totalWeight'
  },
  {
    id: 'totalVolumeM3',
    accessorKey: 'totalVolumeM3',
    header: 'Volume (m³)',
    description: 'Total volume in cubic meters',
    width: 'min-w-[100px]',
    cell: (row) => row.totalVolumeM3?.toFixed(2) || '-',
    enableSorting: true,
    sortFieldId: 'totalVolume'
  },
  {
    id: 'flags',
    header: 'Flags',
    description: 'Special handling flags',
    width: 'min-w-[120px]',
    cell: (row) => {
      const flags = [];
      if (row.coldChain) flags.push('❄️');
      if (row.dangerousGoods) flags.push('⚠️');
      if (row.fragile) flags.push('💎');
      if (row.regulated) flags.push('📋');

      return <span className="text-lg">{flags.join(' ') || '-'}</span>;
    }
  }
];

export const getColumnPresets = (t: TFunction): ColumnPreset[] => [
  {
    id: 'medical-view',
    label: t('Medical View'),
    columnIds: [
      'cmrNumber',
      'statusCode',
      'senderOrgName',
      'receiverOrgName',
      'destinationLocationDisplayName',
      'flags'
    ]
  }
];
